/**
 * The Mail tab: the emails the project's services sent through the sandbox mail relay (vfs-service
 * src/appconfig/mail). With the organisation's SMTP server (set in the Sandbox admin) they go out from the
 * organisation's address, to the person, the allowed domains or addresses, up to a daily limit; without one, they stay
 * here, in the Outbox, readable like a test mail catcher, so the app works the same in the sandbox. Every message is
 * listed whatever happened to it (sent, in the Outbox, refused, failed). Everyone with access sees the list; editors
 * read a message, download it (.eml) and empty the Outbox.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { clearMail, downloadMail, getMail, getMailMessage } from '../../services/AppConfigService';
import { MAIL_LABEL, MAIL_TONE, errorText, when } from './appConfigModel';
import './AppConfig.css';

const FILTERS = [
  { id: null, label: 'All' },
  { id: 'sent', label: 'Sent' },
  { id: 'captured', label: 'In Outbox' },
  { id: 'refused', label: 'Refused' },
  { id: 'failed', label: 'Failed' },
];

/**
 * Who the apps may email (the organisation's policy, set in Flow admin › Settings › Email or the chart): the person,
 * the allowed domains (and their subdomains), single addresses, or anyone. Older sandboxes send no allowedAddresses.
 */
export function recipientsText(relay) {
  const domains = (relay.allowedDomains || []).filter((d) => d !== '*');
  const addresses = relay.allowedAddresses || [];
  if (relay.anyone || (relay.allowedDomains || []).includes('*')) { return 'to any address'; }
  const parts = [];
  if (domains.length) { parts.push(`addresses at ${domains.map((d) => `@${d}`).join(', ')}`); }
  if (addresses.length) { parts.push(addresses.join(', ')); }
  return parts.length ? `to you and ${parts.join(', and ')}` : 'to you only';
}

function RelayState({ relay }) {
  if (!relay) { return null; }
  if (!relay.enabled) { return <p className="cfg-banner is-warn">The mail relay is off in this sandbox: services get no SMTP settings.</p>; }
  if (relay.mode === 'relay') {
    return (
      <p className="cfg-banner is-pass" data-testid="mail-mode">
        Emails go out through your organisation&apos;s SMTP server, from <strong>{relay.from}</strong>, {recipientsText(relay)}. {relay.usedToday} of {relay.dailyLimit} sent today.
      </p>
    );
  }
  return (
    <p className="cfg-banner is-info" data-testid="mail-mode">
      Your organisation has no SMTP server set up{relay.mode === 'unknown' ? ' (or it cannot be reached right now)' : ''}: emails your app sends stay here, in the Outbox. Nobody receives them.
    </p>
  );
}

export default function OutboxPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async() => {
    try {
      setData(await getMail(projectId, { status: filter }));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [projectId, filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [load]);

  const show = async(m) => {
    if (!data?.canRead) { return; }
    try { setOpen(await getMailMessage(projectId, m.id)); } catch (e) { setError(e); }
  };
  const clear = async() => {
    setBusy(true);
    try { await clearMail(projectId); setOpen(null); await load(); } catch (e) { setError(e); } finally { setBusy(false); }
  };

  return (
    <div className="cfg-panel" data-testid="mail-panel">
      <header className="cfg-head">
        <div>
          <h2>Mail</h2>
          <p className="cfg-muted">Emails sent by your app through the sandbox mail relay (SMTP_HOST / SMTP_PORT in its environment).</p>
        </div>
        {data?.canRead && data.messages?.length > 0 && <button className="cfg-btn is-sm" disabled={busy} onClick={clear} type="button">Empty the list</button>}
      </header>
      {error && <p className="cfg-banner is-fail" role="alert">{errorText(error)}</p>}
      {!data && !error && <p className="cfg-muted">Loading…</p>}
      {data && (
        <>
          <RelayState relay={data.relay} />
          <div aria-label="Filter" className="cfg-filters" role="group">
            {FILTERS.map((f) => <button aria-pressed={filter === f.id} className="cfg-chip" key={f.label} onClick={() => { setFilter(f.id); setOpen(null); }} type="button">{f.label}</button>)}
          </div>
          {data.messages.length === 0 && (
            <div className="cfg-state" data-testid="mail-empty">
              <strong>No emails yet</strong>
              <p>When your app sends one (e.g. with nodemailer and SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD from its environment, from FLOW_MAIL_FROM), it shows up here.</p>
            </div>
          )}
          {data.messages.length > 0 && (
            <div className="cfg-mail">
              <ul className="cfg-mail-list" data-testid="mail-list">
                {data.messages.map((m) => (
                  <li key={m.id}>
                    <button aria-current={open?.id === m.id} className={`cfg-mail-item${open?.id === m.id ? ' is-on' : ''}`} data-testid={`mail-${m.id}`} onClick={() => show(m)} type="button">
                      <span className="cfg-row">
                        <strong className="cfg-grow">{m.subject}</strong>
                        <span className={`cfg-pill is-xs is-${MAIL_TONE[m.status] || 'info'}`}>{MAIL_LABEL[m.status] || m.status}</span>
                      </span>
                      <span className="cfg-muted cfg-small">To {m.to.join(', ')} · {m.service} · {when(m.at)}</span>
                      {m.reason && <span className="cfg-small cfg-ink-fail">{m.reason}</span>}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="cfg-mail-view">
                {!data.canRead && <p className="cfg-muted">Editors can open a message.</p>}
                {data.canRead && !open && <p className="cfg-muted">Choose a message to read it.</p>}
                {open && (
                  <article data-testid="mail-message">
                    <h3>{open.content?.subject || open.subject}</h3>
                    <dl className="cfg-mail-meta">
                      <dt>From</dt><dd>{open.content?.from || '—'}</dd>
                      <dt>To</dt><dd>{open.to.join(', ')}</dd>
                      {open.content?.cc && <><dt>Cc</dt><dd>{open.content.cc}</dd></>}
                      <dt>Status</dt><dd>{MAIL_LABEL[open.status] || open.status}{open.reason ? ` — ${open.reason}` : ''}</dd>
                    </dl>
                    {open.content?.html
                      ? <iframe className="cfg-mail-html" sandbox="" srcDoc={open.content.html} title="Message" />
                      : <pre className="cfg-mail-text">{open.content?.text || '(no text)'}</pre>}
                    {open.content?.attachments?.length > 0 && <p className="cfg-muted cfg-small">Attachments: {open.content.attachments.map((a) => a.filename).join(', ')}</p>}
                    <button className="cfg-btn is-sm" onClick={() => downloadMail(projectId, open.id).catch(setError)} type="button">Download .eml</button>
                  </article>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

OutboxPanel.propTypes = { projectId: PropTypes.string.isRequired };
